import { DOCUMENT, DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  Signal,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import {
  DetailsHistorieStatus,
  UebergabeEntwurf,
  UebergabeHandOff,
  UebergabeStufe,
} from '../../models/details-historie.model';
import { AvatarComponent } from '../avatar/avatar.component';
import { DetailsHistorieStatusComponent } from '../details-historie-card/details-historie-status/details-historie-status.component';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { PersonOption, PersonSelectComponent } from '../person-select/person-select.component';

@Component({
  selector: 'app-uebergabe-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslatePipe,
    MatIcon,
    MatTooltip,
    AvatarComponent,
    DetailsHistorieStatusComponent,
    PersonSelectComponent,
  ],
  templateUrl: './uebergabe-panel.component.html',
  styleUrl: './uebergabe-panel.component.scss',
})
export class UebergabePanelComponent {
  readonly chain = input<UebergabeStufe[]>([]);
  readonly closed = input<boolean>(false);
  readonly closedAt = input<string | null>(null);
  readonly vorlagen = input<string[]>([]);
  readonly personOptions = input<PersonOption[]>([]);
  readonly readonly = input<boolean>(false);
  readonly disabledReason = input<string | null>(null);
  readonly skipAllowed = input<boolean>(true);
  readonly busy = input<boolean>(false);
  readonly errorMessage = input<string | null>(null);
  readonly status = input<DetailsHistorieStatus>('bereit');
  readonly fehlerText = input<string | null>(null);
  readonly testIdPrefix = input.required<string>();
  readonly messageRequired = input<boolean>(false);
  readonly activeSince = input<string | null>(null);
  readonly handOffDisabledReason = input<string | null>(null);
  readonly platzhalter = input<string | null>(null);
  /** Optional: hält den Entwurf beim Elternteil, damit er das Zerstören des Panels übersteht. */
  readonly entwurf = model<UebergabeEntwurf | null>(null);

  readonly handOff = output<UebergabeHandOff>();
  // eslint-disable-next-line @angular-eslint/no-output-native -- Name aus der verbindlichen Schnittstelle (Plan NEX-2369)
  readonly close = output<void>();
  readonly reopen = output<void>();
  readonly skip = output<string>();
  readonly retry = output<void>();

  protected readonly zeitMuster = 'dd.MM.yyyy, HH:mm';
  protected readonly nachrichtMax = FIELD_LENGTHS.HANDOVER_MESSAGE_MAX;
  protected readonly formularId = computed(() => `${this.testIdPrefix()}-uebergabe-formular`);
  protected readonly nachrichtId = computed(() => `${this.testIdPrefix()}-uebergabe-nachricht`);
  protected readonly nachrichtFehlerId = computed(() => `${this.testIdPrefix()}-uebergabe-nachricht-fehler`);
  protected readonly ketteId = computed(() => `${this.testIdPrefix()}-uebergabe-kette`);

  protected readonly formularOffen = signal(false);
  protected readonly personControl = new FormControl<string | null>(null);
  protected readonly nachricht = signal('');
  protected readonly benachrichtigen = signal(true);
  protected readonly nachrichtFehlt = signal(false);

  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly personId = toSignal(this.personControl.valueChanges, { initialValue: null });
  private readonly uebergebenKnopf = viewChild<ElementRef<HTMLButtonElement>>('uebergebenKnopf');
  private readonly wiederOeffnenKnopf = viewChild<ElementRef<HTMLButtonElement>>('wiederOeffnenKnopf');
  private readonly stufenBeimAbsenden = signal<string[] | null>(null);
  private entwurfUebernommen = false;
  private fokusNachAbschliessen = false;

  protected readonly ueberspringbar = computed(
    () => this.skipAllowed() && this.chain().filter((stufe) => !stufe.skipped).length > 1,
  );
  protected readonly empfaenger = computed(
    () => this.personOptions().find((person) => person.id === this.personId()) ?? null,
  );
  protected readonly gesperrt = computed(() => this.busy() || !!this.disabledReason());
  protected readonly uebergebenGesperrt = computed(() => this.gesperrt() || !!this.handOffDisabledReason());
  protected readonly uebergebenGrund = computed(() => this.disabledReason() ?? this.handOffDisabledReason() ?? '');
  protected readonly absendenGesperrt = computed(() => !this.empfaenger() || this.uebergebenGesperrt());

  protected readonly aktuell = computed<UebergabeStufe | null>(
    () =>
      this.chain()
        .filter((stufe) => !stufe.skipped)
        .at(-1) ?? null,
  );

  private readonly aktuellerEntwurf = computed<UebergabeEntwurf | null>(() => {
    const entwurf: UebergabeEntwurf = {
      formularOffen: this.formularOffen(),
      userId: this.personId(),
      nachricht: this.nachricht(),
      benachrichtigen: this.benachrichtigen(),
      stufenBeimAbsenden: this.stufenBeimAbsenden(),
    };
    const leer =
      !entwurf.formularOffen &&
      !entwurf.userId &&
      !entwurf.nachricht &&
      entwurf.benachrichtigen &&
      !entwurf.stufenBeimAbsenden;
    return leer ? null : entwurf;
  });

  constructor() {
    effect(() => {
      const entwurf = this.aktuellerEntwurf();
      untracked(() => {
        if (!this.entwurfUebernommen) {
          this.entwurfUebernommen = true;
          this.entwurfUebernehmen(this.entwurf());
        } else if (JSON.stringify(entwurf) !== JSON.stringify(this.entwurf())) {
          this.entwurf.set(entwurf);
        }
      });
    });
    // Der Elternteil verwirft den Entwurf, etwa beim Wechsel des Objekts, während das Panel bestehen bleibt.
    effect(() => {
      const verworfen = this.entwurf() === null;
      if (verworfen && untracked(this.aktuellerEntwurf)) untracked(() => this.formularLeeren());
    });
    // Scheitert das Absenden, verwirft der Elternteil die gemerkten Stufen im Entwurf.
    effect(() => {
      const verworfen = !this.entwurf()?.stufenBeimAbsenden;
      if (verworfen && untracked(this.stufenBeimAbsenden)) this.stufenBeimAbsenden.set(null);
    });
    effect(() => {
      const chain = this.chain();
      const bekannt = untracked(this.stufenBeimAbsenden);
      if (bekannt && chain.some((stufe) => !bekannt.includes(stufe.stageId))) {
        this.formularLeeren();
        this.fokusImPanelSetzen(this.uebergebenKnopf);
      }
    });
    effect(() => {
      if (this.closed() && this.fokusNachAbschliessen) {
        this.fokusNachAbschliessen = false;
        this.fokusImPanelSetzen(this.wiederOeffnenKnopf);
      }
    });
  }

  protected seit(stufe: UebergabeStufe): string {
    if (this.closed()) return this.closedAt() || stufe.createdAt;
    return this.activeSince() || stufe.createdAt;
  }

  protected abschliessen(): void {
    if (this.gesperrt()) return;
    this.formularOffen.set(false);
    this.fokusNachAbschliessen = true;
    this.close.emit();
  }

  protected wiederOeffnen(): void {
    if (!this.gesperrt()) this.reopen.emit();
  }

  protected ueberspringen(stufe: UebergabeStufe): void {
    if (!this.gesperrt()) this.skip.emit(stufe.stageId);
  }

  protected istAktiv(stufe: UebergabeStufe): boolean {
    return !this.closed() && stufe === this.aktuell();
  }

  protected vorname(name: string | null | undefined): string {
    return (name ?? '').trim().split(/\s+/)[0];
  }

  protected formularUmschalten(): void {
    if (!this.uebergebenGesperrt()) this.formularOffen.update((offen) => !offen);
  }

  protected abbrechen(): void {
    if (this.gesperrt()) return;
    this.formularLeeren();
    this.uebergebenKnopf()?.nativeElement.focus();
  }

  protected nachrichtGeaendert(event: Event): void {
    this.nachricht.set((event.target as HTMLTextAreaElement).value);
    this.nachrichtFehlt.set(false);
  }

  protected vorlageAnhaengen(vorlage: string): void {
    if (this.gesperrt()) return;
    const text = this.nachricht().replace(/\s*$/, '');
    this.nachricht.set(text ? `${text} ${vorlage}.` : `${vorlage}.`);
    this.nachrichtFehlt.set(false);
  }

  protected absenden(): void {
    const person = this.empfaenger();
    if (!person || this.absendenGesperrt()) return;
    const text = this.nachricht().trim();
    if (this.messageRequired() && !text) {
      this.nachrichtFehlt.set(true);
      return;
    }
    this.stufenBeimAbsenden.set(this.chain().map((stufe) => stufe.stageId));
    this.handOff.emit({ userId: person.id, ...(text ? { message: text } : {}), notify: this.benachrichtigen() });
  }

  // Nur wenn der Fokus noch im Panel ist oder mit dem entfernten Knopf verloren ging, nicht wenn der Nutzer woanders ist.
  private fokusImPanelSetzen(knopf: Signal<ElementRef<HTMLButtonElement> | undefined>): void {
    afterNextRender(
      () => {
        const aktiv = this.document.activeElement;
        if (aktiv === this.document.body || this.host.nativeElement.contains(aktiv)) knopf()?.nativeElement.focus();
      },
      { injector: this.injector },
    );
  }

  private entwurfUebernehmen(entwurf: UebergabeEntwurf | null): void {
    if (!entwurf) return;
    this.formularOffen.set(entwurf.formularOffen);
    this.personControl.setValue(entwurf.userId);
    this.nachricht.set(entwurf.nachricht);
    this.benachrichtigen.set(entwurf.benachrichtigen);
    this.stufenBeimAbsenden.set(entwurf.stufenBeimAbsenden);
  }

  // Direkt geleert: Kommt das Ergebnis schon beim Aufbau an, ändert sich der berechnete Entwurf nicht und meldet nichts.
  private formularLeeren(): void {
    this.entwurf.set(null);
    this.stufenBeimAbsenden.set(null);
    this.formularOffen.set(false);
    this.personControl.reset(null);
    this.nachricht.set('');
    this.nachrichtFehlt.set(false);
    this.benachrichtigen.set(true);
  }
}
